'use client'

import { useCallback, useEffect, useMemo, useRef, useState } from 'react'

import { RECOMMENDED } from '../../context/ProductListFiltersProvider/constants'
import { useProductListFiltersProvider } from '../../context/ProductListFiltersProvider/useProductListFiltersProvider'
import { sortGridItemsByPrice } from '../../utils/sortGridItemsByPrice'
import { Grid, type GridItem } from '../Grid/Grid'

type Props = {
	groupedGridItems: GridItem[][]
	itemLimit: number
}

type GridElement = {
	id: string
	items: GridItem[]
}

export const GridGroup = ({ groupedGridItems, itemLimit }: Readonly<Props>) => {
	const initialCount = useRef(itemLimit)
	const { filteredItemIdSet, currentOrder, nActiveFilters, hasInitialParams } =
		useProductListFiltersProvider()

	const shouldFilter = nActiveFilters > 0

	const processItems = useCallback(
		(initialItems: GridItem[]) => {
			let items = initialItems
			if (shouldFilter) {
				items = items.filter((item) => filteredItemIdSet.has(item.id))
			}
			if (currentOrder !== RECOMMENDED) {
				items = sortGridItemsByPrice(items, currentOrder)
			}
			return items
		},
		[currentOrder, filteredItemIdSet, shouldFilter]
	)

	const computeGridGroup = useCallback(
		(shouldProcessItems: boolean) => {
			const result: GridElement[] = []
			let renderedCount = 0

			for (let index = 0; index < groupedGridItems.length; index++) {
				let items = shouldProcessItems
					? processItems(groupedGridItems[index])
					: groupedGridItems[index]

				// Slice
				if (renderedCount + items.length > itemLimit) {
					items = items.slice(0, itemLimit - renderedCount)
				}

				if (items.length > 0) {
					result.push({ id: `grid-${index}`, items })

					renderedCount += items.length
					if (renderedCount >= itemLimit) {
						break
					}
				}
			}

			return result
		},
		[groupedGridItems, itemLimit, processItems]
	)

	const initialGridGroups = useMemo<GridElement[]>(
		() =>
			initialCount.current === itemLimit
				? computeGridGroup(hasInitialParams)
				: [],
		[hasInitialParams, itemLimit, computeGridGroup]
	)

	const [processedGridGroups, setProcessedGridGroups] =
		useState(initialGridGroups)

	// Apply filtering on client to avoid generating
	// an HTML without the initial cards
	useEffect(() => {
		setProcessedGridGroups(computeGridGroup(true))
	}, [computeGridGroup])

	return (
		<>
			{processedGridGroups
				.filter((grid) => grid.items.length > 0)
				.map((grid, idx) => (
					<Grid key={grid.id} index={idx} items={grid.items} />
				))}
		</>
	)
}
